import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

// MineraliA capsule: a direction must hold this many pixels before the header
// changes shape, so small scroll corrections never retrigger the animation.
const CAPSULE_COLLAPSE_DISTANCE = 320;
const CAPSULE_EXPAND_DISTANCE = 420;
// The full header also stays until the page has scrolled at least this far past it.
const CAPSULE_COLLAPSE_START = 420;

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * MineraliA: refleja transparent, sticky y data-sticky-state en <html> para que el CSS no use
   * body:has(#header-component[...]). El callback corre antes del siguiente pintado.
   */
  #stateMirror = new MutationObserver(() => this.#mirrorState());

  #mirrorState() {
    const root = document.documentElement;
    root.toggleAttribute('data-header', true);
    root.toggleAttribute('data-header-transparent', this.hasAttribute('transparent'));
    root.toggleAttribute('data-header-sticky', this.hasAttribute('sticky'));
    // Equivale a #header-group > .header-section + .shopify-section (otra sección tras el header).
    const headerSection = this.closest('#header-group > .header-section');
    root.toggleAttribute('data-header-followed', !!headerSection?.nextElementSibling?.matches('.shopify-section'));
    if (this.dataset.stickyState) {
      // Sólo si cambia: reescribir un atributo de <html> invalida los estilos de toda la página.
      if (root.getAttribute('data-header-sticky-state') !== this.dataset.stickyState) {
        root.setAttribute('data-header-sticky-state', this.dataset.stickyState);
      }
    } else {
      root.removeAttribute('data-header-sticky-state');
    }
  }

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** MineraliA capsule: scrollTop where the current scroll direction started. */
  #capsuleRunStart = 0;

  /** MineraliA capsule: direction of the current run (1 down, -1 up, 0 none). */
  #capsuleRunDirection = 0;

  /** @type {Element | null | undefined} */
  #footer;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    // MineraliA: sólo se escribe si cambia; cada escritura en <body> invalida los estilos de toda la página.
    if (document.body.style.getPropertyValue('--header-height') !== `${roundedHeaderHeight}px`) {
      document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    }

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    this.#scheduleMeasureCapsule();
  });

  /** MineraliA: mide la cápsula en el siguiente fotograma, no justo después de escribir estilos. */
  #capsuleFrame = 0;
  #scheduleMeasureCapsule = () => {
    if (this.#capsuleFrame) return;
    this.#capsuleFrame = requestAnimationFrame(() => {
      this.#capsuleFrame = 0;
      this.#measureCapsule();
    });
  };

  /**
   * MineraliA: measures the logo so the sticky capsule (see header.liquid)
   * wraps it near the top edge at every breakpoint. Uses offset* metrics,
   * which ignore transforms, so it is safe to run while the logo is animating.
   */
  #measureCapsule = () => {
    if (this.getAttribute('sticky') !== 'always') return;

    const logo = this.querySelector('.header-logo');
    if (!(logo instanceof HTMLElement)) return;
    const image = logo.querySelector('.header-logo__image-container') ?? logo;
    if (!(image instanceof HTMLElement)) return;

    /** @param {HTMLElement} element */
    const pageOffset = (element) => {
      let x = 0;
      let y = 0;
      for (let node = element; node instanceof HTMLElement; node = node.offsetParent) {
        x += node.offsetLeft;
        y += node.offsetTop;
      }
      return { x, y };
    };
    const header = pageOffset(this);
    const logoOffset = pageOffset(logo);
    const imageOffset = pageOffset(image);
    const headerRect = { left: header.x, top: header.y, width: this.offsetWidth };
    const logoRect = {
      left: logoOffset.x,
      top: logoOffset.y,
      width: logo.offsetWidth,
      height: logo.offsetHeight,
    };
    const imageRect = {
      left: imageOffset.x,
      top: imageOffset.y,
      width: image.offsetWidth,
      height: image.offsetHeight,
    };
    if (!imageRect.width || !imageRect.height) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const capsuleHeight = isMobile ? 46 : 50;
    const capsuleTop = isMobile ? 8 : 10;
    const scale = Math.min(1, (capsuleHeight - 16) / imageRect.height);
    const capsuleWidth = Math.round(imageRect.width * scale + (isMobile ? 44 : 52));

    // The logo scales around its own center; follow the image to its scaled position.
    const logoCenterX = logoRect.left + logoRect.width / 2;
    const logoCenterY = logoRect.top + logoRect.height / 2;
    const centerX = logoCenterX + (imageRect.left + imageRect.width / 2 - logoCenterX) * scale - headerRect.left;
    const centerY = logoCenterY + (imageRect.top + imageRect.height / 2 - logoCenterY) * scale - headerRect.top;
    const shiftY = Math.round(capsuleTop + capsuleHeight / 2 - centerY);

    const left = Math.max(8, Math.round(centerX - capsuleWidth / 2));
    this.style.setProperty('--mineralia-capsule-logo-scale', String(Math.round(scale * 1000) / 1000));
    this.style.setProperty('--mineralia-capsule-logo-shift', `${shiftY}px`);
    this.style.setProperty('--mineralia-capsule-top', `${capsuleTop}px`);
    this.style.setProperty('--mineralia-capsule-height', `${capsuleHeight}px`);
    this.style.setProperty('--mineralia-capsule-left', `${left}px`);
    this.style.setProperty('--mineralia-capsule-right', `${Math.max(8, Math.round(headerRect.width - left - capsuleWidth))}px`);
  };

  /** @returns {Element | null} The theme footer, whose top edge the header must never cross. */
  get #footerElement() {
    if (this.#footer === undefined || (this.#footer && !this.#footer.isConnected)) {
      this.#footer = document.querySelector('.shopify-section-group-footer-group')?.closest('footer') ?? null;
    }
    return this.#footer;
  }

  /**
   * MineraliA: decides between the full header and the capsule.
   * - Near the top of the page, or once the footer is in view, the header is always full.
   * - Otherwise it only changes after scrolling far enough in one direction.
   * - When the footer reaches the header, it pushes the header up so they meet edge to edge.
   * @param {number} scrollTop
   * @param {number} previousScrollTop
   */
  #updateCapsule(scrollTop, previousScrollTop) {
    if (this.getAttribute('sticky') !== 'always') return;

    const direction = Math.sign(scrollTop - previousScrollTop);
    if (direction !== 0 && direction !== this.#capsuleRunDirection) {
      this.#capsuleRunDirection = direction;
      this.#capsuleRunStart = previousScrollTop;
    }
    const travelled = Math.abs(scrollTop - this.#capsuleRunStart);

    const headerGroupHeight =
      parseFloat(document.body.style.getPropertyValue('--header-group-height')) || this.offsetHeight;
    const footerTop = this.#footerElement?.getBoundingClientRect().top ?? Infinity;
    const footerInView = footerTop < window.innerHeight;

    let compact = this.hasAttribute('data-capsule');
    // The full header stays until the page has scrolled well past it.
    const collapseStart = headerGroupHeight + Math.max(this.offsetHeight * 2, CAPSULE_COLLAPSE_START);
    if (scrollTop <= collapseStart || footerInView) {
      compact = false;
    } else if (direction > 0 && travelled >= CAPSULE_COLLAPSE_DISTANCE) {
      compact = true;
    } else if (direction < 0 && travelled >= CAPSULE_EXPAND_DISTANCE) {
      compact = false;
    }

    this.toggleAttribute('data-capsule', compact);

    const push = this.dataset.stickyState === 'active' ? Math.min(0, Math.round(footerTop - this.offsetHeight)) : 0;
    const pushValue = `${push}px`;
    if (this.style.getPropertyValue('--mineralia-header-push') !== pushValue) {
      this.style.setProperty('--mineralia-header-push', pushValue);
    }
  }

  /**
   * MineraliA: tapping the capsule expands the full header instead of following the logo link.
   * @param {MouseEvent} event
   */
  #handleCapsuleClick = (event) => {
    if (!this.hasAttribute('data-capsule')) return;
    event.preventDefault();
    event.stopPropagation();
    this.removeAttribute('data-capsule');
    this.#capsuleRunStart = getScrollTop();
    this.#capsuleRunDirection = 0;
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');

    // MineraliA: the new scroll container has its own position and fires no scroll
    // event, so re-evaluate the capsule from where it is now.
    if (stickyMode === 'always') {
      const scrollTop = getScrollTop();
      this.#lastScrollTop = scrollTop;
      this.#capsuleRunStart = scrollTop;
      this.#capsuleRunDirection = 0;
      this.#updateCapsule(scrollTop, scrollTop);
      if (scrollTop <= 0) this.dataset.scrollDirection = 'none';
    }
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.#updateCapsule(scrollTop, this.#lastScrollTop);

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#mirrorState();
    this.#stateMirror.observe(this, { attributes: true, attributeFilter: ['transparent', 'sticky', 'data-sticky-state'] });
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('click', this.#handleCapsuleClick, true);
    window.addEventListener('load', this.#scheduleMeasureCapsule, { once: true });

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stateMirror.disconnect();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('click', this.#handleCapsuleClick, true);
    window.removeEventListener('load', this.#scheduleMeasureCapsule);
    cancelAnimationFrame(this.#capsuleFrame);
    this.#capsuleFrame = 0;
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      // MineraliA: sólo si cambia; cada escritura en <body> invalida los estilos de toda la página.
      if (document.body.style.getPropertyValue('--header-group-height') !== `${roundedHeaderGroupHeight}px`) {
        document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
      }
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
